import { CheckOutlined, EditOutlined, LoadingOutlined, PlusOutlined } from '@ant-design/icons'
import { useSecurityContext } from '@components/Navigation/SecurityContext'
import { GraviButton, Horizontal, Texto } from '@gravitate-js/excalibrr'
import { type FormInstance, Popconfirm } from 'antd'

import { AttachmentsButton } from './BOL/Header/AttachmentsButton'
import { DeliveryTicketButton } from './Drops/DeliveryTicketButton'
import type { DeliveryTicketModalProps } from './Drops/DeliveryTicketModal'
import { getAttachmentCount } from './util'

type MovementHeaderProps = {
  title: string
  editing: boolean
  setEditing: (editing: boolean) => void
  addItem: () => void
  allowCancel?: boolean
  onCancel?: () => void
  setShowingTicketModal: (show: boolean) => void
  setDeliveryTicketData: (data: DeliveryTicketModalProps['data']) => void
  orderNumber?: string
  detail?: Gravitate.OrderMovementDetails
  saveLoading: boolean
  popConfirmTitle: string
  setPopConfirmTitle: (title: string) => void
  saveRequest: () => void
  form: FormInstance
  handleOpenDropAttachmentsModal?: () => void
  handleOpenImageModal?: () => void
}

export function MovementHeader({
  title,
  editing,
  setEditing,
  addItem,
  allowCancel,
  onCancel,
  setShowingTicketModal,
  setDeliveryTicketData,
  orderNumber,
  detail,
  saveLoading,
  popConfirmTitle,
  setPopConfirmTitle,
  saveRequest,
  form,
  handleOpenDropAttachmentsModal,
  handleOpenImageModal,
}: MovementHeaderProps) {
  const { scopes } = useSecurityContext()
  const canEditOrderMovements = scopes?.order_movements?.edit
  const ticketCount = new Set((detail?.store_drops ?? []).map((drop) => drop.store)).size
  const { dropImageCount, bolImageCount } = getAttachmentCount(detail)

  return (
    <div className='detail-section'>
      <div className='mt-1'>
        <Horizontal verticalCenter>
          <Texto category='heading-small' className='mr-3'>
            {title}
          </Texto>
          {title === 'SITE DROPS' && (
            <>
              <DeliveryTicketButton
                onClick={() => {
                  setDeliveryTicketData({
                    selectedOrder: orderNumber,
                    drops: detail?.store_drops,
                  })
                  setShowingTicketModal(true)
                }}
                numTickets={ticketCount || 0}
              />
              <AttachmentsButton
                onClick={handleOpenDropAttachmentsModal}
                numAttachments={dropImageCount}
                tooltipMessage='View Drop Attachments'
                disabled={!detail?.store_drops?.length}
              />
            </>
          )}
          {title === 'BOLs' && (
            <AttachmentsButton
              onClick={handleOpenImageModal}
              numAttachments={bolImageCount}
              tooltipMessage='View BOL Attachments'
              disabled={!detail?.bols?.length}
            />
          )}
        </Horizontal>
      </div>
      <div>
        {editing && canEditOrderMovements && (
          <>
            <GraviButton
              onClick={addItem}
              className='mr-3'
              shape='circle'
              appearance='outline'
              icon={<PlusOutlined className='add-bol' />}
            />
            {allowCancel && (
              <Popconfirm title='Sure to cancel?' onConfirm={() => onCancel()} placement='left'>
                <GraviButton className='mr-3' buttonText='Cancel' appearance='outline' />
              </Popconfirm>
            )}
            <Popconfirm
              title={popConfirmTitle}
              visible={popConfirmTitle !== ''}
              onConfirm={() => {
                saveRequest()
                setPopConfirmTitle('')
              }}
              onCancel={() => setPopConfirmTitle('')}
              okText='Save All'
              cancelText='Cancel'
            >
              <GraviButton
                loading={saveLoading}
                success
                className='pr-3'
                buttonText='Save All'
                icon={saveLoading ? <LoadingOutlined /> : <CheckOutlined />}
                onClick={() => form.submit()}
                data-cy='drop-save-all'
              />
            </Popconfirm>
          </>
        )}
        {!editing && canEditOrderMovements && (
          <GraviButton
            className='mr-1'
            icon={<EditOutlined className='edit-movement' />}
            onClick={() => setEditing(true)}
          />
        )}
      </div>
    </div>
  )
}
